import { useCallback, useState } from 'react';
import Button from '@common/components/controls/Button';
import IconButton from '@common/components/controls/IconButton';
import GuidedTourView from '@common/components/GuidedTourPopup/GuidedTourView';
import IconMessage from '@common/components/Messages/IconMessage';
import PopUp from '@common/components/PopUp';
import IconClose from '@common/components/svgs/common/Close';
import IconFlag from '@common/components/svgs/common/Flag';
import Translated from '@common/components/Translated';
import { type GuidedTour } from '@common/types/guidedTour';

interface NewFeaturePopupProps {
  steps: GuidedTour['steps'];
  onClose: () => void;
}

function NewFeaturePopup({ steps, onClose }: Readonly<NewFeaturePopupProps>) {
  const [step, setStep] = useState(0);
  const tourStep = steps[step];

  const stepForward = useCallback(() => {
    setStep((currentStep) => Math.min(steps.length - 1, currentStep + 1));
  }, [steps]);
  const stepBack = useCallback(() => {
    setStep((currentStep) => Math.max(0, currentStep - 1));
  }, []);

  return (
    <PopUp
      title={(
        <span className="justify-content-space-between">
          <IconMessage
            title={(
              <Translated
                id="new-feature.title"
                tagName="strong"
                values={{ count: steps.length }}
              />
            )}
            component={IconFlag}
          />
          <IconButton icon={IconClose} onClick={onClose} color="regular" />
        </span>
      )}
      titleTag="h4"
      actionButton={(
        <>
          {step > 0 && steps.length > 1 && (
            <Button
              type="button"
              mode="text"
              onClick={stepBack}
              data-testid="new-feature.step-prev"
            >
              <Translated id="new-feature.button.prev" />
            </Button>
          )}
          {step >= 0 && step < steps.length - 1 && steps.length > 1 && (
            <Button
              onClick={stepForward}
              data-testid="new-feature.next"
            >
              <Translated id="new-feature.button.next" />
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              onClick={onClose}
              data-testid="new-feature.finish"
            >
              <Translated id="new-feature.button.got-it" />
            </Button>
          )}
        </>
      )}
      close={onClose}
      type="small"
      noCancelButton
    >
      <GuidedTourView step={tourStep} />
    </PopUp>
  );
}

export default NewFeaturePopup;
